import FormatCurrency from 'components/Common/FormatCurrency'
import LineDoorIcon from 'LuxKit/Icons/line/LineDoorIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import SwitchListItem from 'LuxKit/List/SwitchListItem'
import Sheet from 'components/utils/Sheet'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { Fragment, useCallback, useContext } from 'react'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import Divider from 'LuxKit/Divider'
import { rem } from 'polished'
import styled from 'styled-components'
import TextLink from 'LuxKit/TextLink'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'

const DividerContainer = styled.div`
  padding-inline: ${rem(16)};
`

interface Props {
  rooms: Array<OfferPageRoom>
  roomsConnected?: boolean
  flights?: boolean
  flightsEnabled?: boolean
  airport?: App.Airport
  totalFlightPrice?: number
  charterBundleRoute?: App.CharterBundleRoute
  bundledWithFlightsOnly?: boolean
}

function OfferPageTogglesSection({
  rooms,
  roomsConnected,
  flights,
  flightsEnabled,
  airport,
  totalFlightPrice,
  charterBundleRoute,
  bundledWithFlightsOnly,
}: Props) {
  const hasMultipleRooms = useContext(OfferPageStateContext).rooms.length > 1

  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const onConnectedRoomsChange = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleConnectedRooms })
  }, [dispatch])

  const onFlightsChange = useCallback(() => {
    dispatch({ type: OfferPageActions.setFlights, flights: !flights })
  }, [dispatch, flights])

  const changeAirport = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
    trackEvent('AIRPORT_FILTER_PRESSED')
  }, [dispatch, trackEvent])

  let flightPricePerPerson: number | undefined = undefined
  if (totalFlightPrice) {
    const flightOccupants = countOccupantsForFlights(rooms)
    const totalPeople = flightOccupants.adults + flightOccupants.children + flightOccupants.infants
    flightPricePerPerson = totalFlightPrice / totalPeople
  }

  const toggles = [
    {
      key: 'connectingRooms',
      visible: hasMultipleRooms && !charterBundleRoute,
      element: <SwitchListItem
        title="Request connecting rooms"
        subtitle="Subject to availability. Confirmed at check-in."
        icon={<LineDoorIcon />}
        defaultChecked={roomsConnected}
        inputPlacement="end"
        onChange={onConnectedRoomsChange}
      />,
    },
    {
      key: 'flights',
      visible: flightsEnabled && !charterBundleRoute,
      element: <SwitchListItem
        title={<>{bundledWithFlightsOnly ? 'Select' : 'Add'} flights {airport && <>from <TextLink onClick={changeAirport}>{airport.code}</TextLink></>}</>}
        subtitle={flightPricePerPerson && !bundledWithFlightsOnly ? <>From <FormatCurrency value={flightPricePerPerson} format="rounded" />/adult</> : undefined}
        icon={<LinePlaneDepartureIcon />}
        defaultChecked={bundledWithFlightsOnly ? true : flights}
        checked={bundledWithFlightsOnly ? true : undefined}
        inputPlacement="end"
        onChange={bundledWithFlightsOnly ? undefined : onFlightsChange}
      />,
    },
  ].filter(toggle => toggle.visible)

  if (toggles.length === 0) {
    return null
  }

  return <Sheet background="neutral-eight">
    {toggles.map((toggle, i) => <Fragment key={toggle.key}>
      {i > 0 && <DividerContainer><Divider kind="primary" /></DividerContainer>}
      {toggle.element}
    </Fragment>)}
  </Sheet>
}

type MappedProps = Pick<Props,
  | 'rooms'
  | 'airport'
  | 'roomsConnected'
  | 'flights'
  | 'flightsEnabled'
  | 'charterBundleRoute'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  rooms: state.rooms,
  airport: state.airport,
  roomsConnected: state.isMultiRoomConnected,
  flights: state.flights,
  flightsEnabled: offerFlightsEnabled(state, appState),
  charterBundleRoute: state.charterBundleRoute,
}), OfferPageTogglesSection)
